import { Text } from '@mantine/core';
import { useState } from 'react';
import { Card } from '../../../components/common/Card';
import type { ExamQuestion } from './types';
import { QuestionHeader } from './QuestionHeader';
import { QuestionImageFromApi } from './QuestionImageFromApi';
import { QuestionOptions } from './QuestionOptions';
import { examQuestionUiType } from '../utils/examQuestionUi';
import classes from '../css/QuestionCard.module.css';

function hasQuestionImage(q: ExamQuestion): boolean {
  const p = q.imagePath;
  return p != null && String(p).trim() !== '';
}

interface Props {
  question: ExamQuestion;
  /** Sequential number to display in the header (1-based). Falls back to questionId. */
  displayNumber?: number;
  answer?: any;
  onAnswerChange?: (value: any) => void;
  flagged?: boolean;
  onFlagToggle?: () => void;
}

export function QuestionCard({
  question,
  displayNumber,
  answer: controlledAnswer,
  onAnswerChange,
  flagged = false,
  onFlagToggle,
}: Props) {
  const uiType = examQuestionUiType(question);
  const [internalAnswer, setInternalAnswer] = useState<any>(() =>
    uiType === 'match_pair' ? {} : ''
  );
  const isControlled = controlledAnswer !== undefined && onAnswerChange != null;
  const answer = isControlled ? controlledAnswer : internalAnswer;

  const handleChange = (value: any) => {
    if (onAnswerChange) onAnswerChange(value);
    if (!isControlled) setInternalAnswer(value);
  };

  return (
    <Card className={classes.card}>
      <QuestionHeader
        number={displayNumber ?? question.questionId}
        marks={question.marks}
        type={uiType}
        flagged={flagged}
        onFlagClick={onFlagToggle}
      />
      <Text className={classes.questionText} mt="md" fw={600}>
        {question.questionText}
      </Text>
      {hasQuestionImage(question) ? (
        <QuestionImageFromApi questionId={question.questionId} />
      ) : null}
      <QuestionOptions
        uiType={uiType}
        questionId={question.questionId}
        options={question.options ?? []}
        value={answer}
        onChange={handleChange}
        matchPairs={question.matchPairs}
      />
    </Card>
  );
}
